import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { warehouses, warehouseItems, customers } from '../../services/api';
import WarehouseSelect from './WarehouseSelect';
import ItemSelect from './ItemSelect';
import CustomerSelect from '../customers/CustomerSelect';
import CustomerModal from '../customers/CustomerModal';
import { useAuth } from '../../context/AuthContext';

const MaterialDeliveryModal = ({ 
    isOpen, 
    onClose, 
    onSave, 
    initialCustomerId = null,
    initialCustomerName = null,
    projectId = null,
    isFromProject = false,
    delivery = null
}) => {
    const [loading, setLoading] = useState(true);
    const [warehousesList, setWarehouses] = useState([]);
    const [itemsList, setItems] = useState([]);
    const [customersList, setCustomers] = useState([]);
    const [error, setError] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    const { user } = useAuth();

    const [deliveryData, setDeliveryData] = useState({
        date: delivery?.date ? new Date(delivery.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        sourceWarehouseId: delivery?.sourceWarehouse?._id || '',
        targetWarehouseId: delivery?.targetWarehouse?._id || '',
        customerId: delivery?.customerId?._id || initialCustomerId || '',
        isCustomerDelivery: delivery ? delivery.isCustomerDelivery : isFromProject ? true : false,
        items: delivery?.items.map(item => ({
            itemId: item.item._id,
            quantity: item.quantity,
            averagePrice: item.averagePrice,
            exceedsStock: false
        })) || []
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialCustomerId) {
            setDeliveryData(prev => ({
                ...prev,
                isCustomerDelivery: true,
                customerId: initialCustomerId
            }));
        }
    }, [initialCustomerId]);

    const fetchData = async () => {
        try {
            const [warehousesRes, itemsRes, customersRes] = await Promise.all([
                warehouses.getAll(),
                warehouseItems.getAll(),
                customers.getAll()
            ]);
            setWarehouses(warehousesRes.data);
            setItems(itemsRes.data);
            setCustomers(customersRes.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = () => {
        setDeliveryData({
            ...deliveryData,
            items: [
                ...deliveryData.items,
                { itemId: '', quantity: '', averagePrice: 0, exceedsStock: false }
            ]
        });
    };

    const handleRemoveItem = (index) => {
        const newItems = deliveryData.items.filter((_, i) => i !== index);
        setDeliveryData({ ...deliveryData, items: newItems });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...deliveryData.items];
        const updatedItem = { ...newItems[index], [field]: value };

        if (field === 'itemId') {
            const selectedItem = itemsList.find(item => item._id === value);
            if (selectedItem) {
                updatedItem.averagePrice = selectedItem.price || 0;
                updatedItem.quantity = '';
                updatedItem.exceedsStock = false;
            }
        }

        if (field === 'quantity') {
            const item = itemsList.find(i => i._id === newItems[index].itemId);
            const warehouseAssignment = item?.assignedWarehouses.find(
                a => a.warehouse._id === deliveryData.sourceWarehouseId
            );
            const availableStock = warehouseAssignment?.amount || 0;
            updatedItem.exceedsStock = Number(value) > availableStock;
        }

        newItems[index] = updatedItem;
        setDeliveryData({ ...deliveryData, items: newItems });
    };

    const getAvailableStock = (itemId) => {
        const item = itemsList.find(i => i._id === itemId);
        const warehouseAssignment = item?.assignedWarehouses.find(
            a => a.warehouse._id === deliveryData.sourceWarehouseId
        );
        return warehouseAssignment?.amount || 0;
    };

    const validateForm = () => {
        const newErrors = {};
        if (!deliveryData.sourceWarehouseId) newErrors.sourceWarehouseId = 'Source warehouse is required';
        if (!deliveryData.isCustomerDelivery && !deliveryData.targetWarehouseId) {
            newErrors.targetWarehouseId = 'Target warehouse is required';
        }
        if (deliveryData.isCustomerDelivery && !deliveryData.customerId) {
            newErrors.customerId = 'Customer is required';
        }
        if (deliveryData.items.length === 0) newErrors.items = 'At least one item is required';

        deliveryData.items.forEach((item, index) => {
            if (!item.itemId) newErrors[`item-${index}`] = 'Select an item';
            if (!item.quantity || item.quantity <= 0) newErrors[`quantity-${index}`] = 'Enter valid quantity';
            if (item.exceedsStock) newErrors[`quantity-${index}`] = 'Quantity exceeds available stock';
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCustomerSave = async (customerData) => {
        try {
            const { data } = await customers.create({
                ...customerData,
                createdBy: user.id
            });
            
            await fetchData();
            
            setDeliveryData(prev => ({
                ...prev,
                customerId: data._id
            }));
            
            setShowCustomerModal(false);
            return true;
        } catch (err) {
            const errorMessage = err.response?.data?.message || 
                               err.message || 
                               'Failed to create customer';
            setError(errorMessage);
            return false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const dataToSave = {
                ...deliveryData,
                projectId: projectId,
                status: delivery?.status || (projectId ? 'InProgress' : 'Completed'),
                isCustomerDelivery: isFromProject ? true : deliveryData.isCustomerDelivery
            };
            onSave(dataToSave);
        }
    };

    if (!isOpen) return null;

    if (loading) {
        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                        {delivery ? 'Update Material Delivery' : 'New Material Delivery'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Date *</label>
                            <input
                                type="date"
                                className="mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={deliveryData.date}
                                onChange={(e) => setDeliveryData({ ...deliveryData, date: e.target.value })}
                            />
                        </div>

                        <div className="sm:col-span-2">
                            <WarehouseSelect
                                warehouses={warehousesList}
                                selectedWarehouse={deliveryData.sourceWarehouseId}
                                onChange={(warehouseId) => setDeliveryData({ ...deliveryData, sourceWarehouseId: warehouseId })}
                                label="Source Warehouse *"
                            />
                            {errors.sourceWarehouseId && (
                                <p className="mt-1 text-sm text-red-500">{errors.sourceWarehouseId}</p>
                            )}
                        </div>

                        {!isFromProject && (
                            <div className="sm:col-span-2">
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <span className="text-sm font-medium text-gray-700">Delivery Type</span>
                                    <div className="flex items-center gap-4">
                                        <label className={`flex items-center cursor-pointer ${!deliveryData.isCustomerDelivery ? 'text-blue-600' : 'text-gray-500'}`}>
                                            <input
                                                type="radio"
                                                className="sr-only"
                                                checked={!deliveryData.isCustomerDelivery}
                                                onChange={() => setDeliveryData({
                                                    ...deliveryData,
                                                    isCustomerDelivery: false,
                                                    customerId: '',
                                                    targetWarehouseId: ''
                                                })}
                                            />
                                            <span className={`px-3 py-2 rounded-md ${!deliveryData.isCustomerDelivery ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'}`}>
                                                Warehouse
                                            </span>
                                        </label>
                                        <label className={`flex items-center cursor-pointer ${deliveryData.isCustomerDelivery ? 'text-blue-600' : 'text-gray-500'}`}>
                                            <input
                                                type="radio"
                                                className="sr-only"
                                                checked={deliveryData.isCustomerDelivery}
                                                onChange={() => setDeliveryData({
                                                    ...deliveryData,
                                                    isCustomerDelivery: true,
                                                    customerId: '',
                                                    targetWarehouseId: ''
                                                })}
                                            />
                                            <span className={`px-3 py-2 rounded-md ${deliveryData.isCustomerDelivery ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'}`}>
                                                Customer
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="sm:col-span-2">
                            {deliveryData.isCustomerDelivery ? (
                                isFromProject ? (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                                        <div className="p-3 bg-gray-50 rounded-md">
                                            <div className="text-sm text-gray-900">
                                                {initialCustomerName || customersList.find(c => c._id === initialCustomerId)?.name}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <CustomerSelect
                                        customers={customersList}
                                        selectedCustomer={deliveryData.customerId}
                                        onChange={(customerId) => setDeliveryData({ ...deliveryData, customerId })}
                                        onAddNewClick={() => setShowCustomerModal(true)}
                                    />
                                )
                            ) : (
                                <WarehouseSelect
                                    warehouses={warehousesList.filter(w => w._id !== deliveryData.sourceWarehouseId)}
                                    selectedWarehouse={deliveryData.targetWarehouseId}
                                    onChange={(warehouseId) => setDeliveryData({ ...deliveryData, targetWarehouseId: warehouseId })}
                                    label="Target Warehouse *"
                                />
                            )}
                            {errors.customerId && (
                                <p className="mt-1 text-sm text-red-500">{errors.customerId}</p>
                            )}
                            {errors.targetWarehouseId && (
                                <p className="mt-1 text-sm text-red-500">{errors.targetWarehouseId}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="text-sm font-medium text-gray-900">Items</h4>
                            <button
                                type="button"
                                onClick={handleAddItem}
                                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Item
                            </button>
                        </div>

                        {errors.items && (
                            <p className="mb-4 text-sm text-red-500">{errors.items}</p>
                        )}

                        <div className="space-y-4">
                            {deliveryData.items.map((item, index) => (
                                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-4">
                                        <div className="sm:col-span-3">
                                            <ItemSelect
                                                items={itemsList}
                                                selectedItem={item.itemId}
                                                onChange={(itemId) => handleItemChange(index, 'itemId', itemId)}
                                            />
                                            {errors[`item-${index}`] && (
                                                <p className="mt-1 text-xs text-red-500">{errors[`item-${index}`]}</p>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium text-gray-700">Quantity *</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className={`mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                                                    errors[`quantity-${index}`] || item.exceedsStock ? 'border-red-500' : ''
                                                }`}
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                            />
                                            {item.itemId && (
                                                <p className={`text-xs ${item.exceedsStock ? 'text-red-500' : 'text-gray-500'}`}>
                                                    Available stock: {getAvailableStock(item.itemId)}
                                                    {item.exceedsStock && ' - Exceeds available stock'}
                                                </p>
                                            )}
                                            {errors[`quantity-${index}`] && (
                                                <p className="text-xs text-red-500">{errors[`quantity-${index}`]}</p>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleRemoveItem(index)}
                                        className="p-1 text-red-600 hover:text-red-800"
                                    >
                                        <Trash2 className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}

                            {deliveryData.items.length === 0 && (
                                <div className="text-center py-4 text-sm text-gray-500">
                                    No items added yet. Click "Add Item" to start.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                            {delivery ? 'Update Delivery' : 'Create Delivery'}
                        </button>
                    </div>
                </form>

                {showCustomerModal && (
                    <CustomerModal
                        isOpen={showCustomerModal}
                        onClose={() => setShowCustomerModal(false)}
                        onSave={handleCustomerSave}
                        customer={null}
                    />
                )}
            </div>
        </div>
    );
};

export default MaterialDeliveryModal;